import { Lock } from 'lucide-react';
import LegalPage from '../components/LegalPage';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    body: (
      <>
        <p>
          This Privacy Policy explains how RoomHora (&ldquo;RoomHora&rdquo;,
          &ldquo;we&rdquo;, &ldquo;our&rdquo;) collects, uses, stores, and shares
          information when you use our website, mobile applications, and
          related services (collectively, the &ldquo;Services&rdquo;).
        </p>
        <p>
          By using RoomHora you agree to the practices described in this
          policy. If you do not agree, please do not use our Services.
        </p>
      </>
    ),
  },
  {
    id: 'information-we-collect',
    title: '2. Information We Collect',
    body: (
      <>
        <p>
          <strong>Account data.</strong> When you sign up we collect your name,
          email address, phone number, password (stored as a salted bcrypt
          hash — we never store passwords in plain text), and optionally a
          profile photo.
        </p>
        <p>
          <strong>Listing data.</strong> Property owners provide listing
          details: title, description, price, location, photos, amenities, and
          rental terms.
        </p>
        <p>
          <strong>Usage data.</strong> Searches, saved favorites, inquiries,
          and bookings. We use this to show you relevant listings and to
          improve the product.
        </p>
        <p>
          <strong>Location data.</strong> If you grant permission, we use your
          device&rsquo;s location to help find nearby rentals. You can revoke
          this at any time in your browser or OS settings.
        </p>
        <p>
          <strong>Technical data.</strong> IP address, browser type, device
          information, and cookie identifiers — used for security, rate
          limiting, and basic analytics.
        </p>
      </>
    ),
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    body: (
      <>
        <ul className="list-disc pl-5 space-y-2">
          <li>To operate and maintain the RoomHora platform.</li>
          <li>To connect renters with property owners.</li>
          <li>To verify ownership, prevent fraud, and enforce our Terms.</li>
          <li>To send transactional emails (booking confirmations, password resets, security alerts).</li>
          <li>To improve our product through aggregated, anonymized analytics.</li>
          <li>To comply with legal obligations.</li>
        </ul>
        <p>
          We do <strong>not</strong> sell your personal data to third parties.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    title: '4. How We Share Information',
    body: (
      <>
        <p>We share information only in these limited cases:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Between platform users.</strong> When a renter inquires
            about a listing, the owner sees the inquirer&rsquo;s name and the
            message content; renters see owner-provided contact details.
          </li>
          <li>
            <strong>Service providers.</strong> Infrastructure (hosting,
            database), email delivery, SMS OTP, payment processing, and error
            monitoring. Each provider is bound by a data processing agreement.
          </li>
          <li>
            <strong>Legal requirements.</strong> If required by law, valid
            subpoena, or to protect the safety of our users.
          </li>
          <li>
            <strong>Business transfers.</strong> In the event of a merger,
            acquisition, or sale of assets, with notice to users.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'retention',
    title: '5. Data Retention',
    body: (
      <>
        <p>
          We keep account data for as long as your account is active. If you
          delete your account, we hard-delete your personal data (favorites,
          notifications, messages) immediately, anonymize your reviews so
          aggregate ratings remain intact, and redact your personal details
          from booking records that owners need for their own tax/history
          purposes.
        </p>
        <p>
          Server logs and fraud-prevention records may be retained for up to
          12 months.
        </p>
      </>
    ),
  },
  {
    id: 'your-rights',
    title: '6. Your Rights',
    body: (
      <>
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Access the personal data we hold about you.</li>
          <li>Correct inaccurate data.</li>
          <li>Delete your account and associated data.</li>
          <li>Export your data in a portable format.</li>
          <li>Object to or restrict certain processing.</li>
          <li>Withdraw consent where processing is based on consent.</li>
        </ul>
        <p>
          You can delete your account at any time from{' '}
          <strong>Settings → Account → Delete Account</strong>, or by emailing{' '}
          <a href="mailto:privacy@roomhora.com" className="text-accent hover:underline">
            privacy@roomhora.com
          </a>.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: '7. Security',
    body: (
      <p>
        We protect your data with TLS-encrypted connections, bcrypt-hashed
        passwords, rate-limited authentication, server-side input
        sanitization, and environment-isolated secrets. No system is perfectly
        secure — we&rsquo;ll notify affected users promptly if a breach occurs.
      </p>
    ),
  },
  {
    id: 'children',
    title: '8. Children',
    body: (
      <p>
        RoomHora is not directed at children under 16 and we do not knowingly
        collect data from them. If you believe a child has provided us with
        personal information, please contact us and we will delete it.
      </p>
    ),
  },
  {
    id: 'international',
    title: '9. International Transfers',
    body: (
      <p>
        Your data may be processed in countries other than the one where you
        live. We take appropriate safeguards (standard contractual clauses,
        secure hosting) to ensure adequate protection.
      </p>
    ),
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    body: (
      <p>
        We may update this policy from time to time. Material changes will be
        notified via email or an in-product banner at least 14 days before
        they take effect.
      </p>
    ),
  },
  {
    id: 'contact',
    title: '11. Contact',
    body: (
      <p>
        Privacy questions:{' '}
        <a href="mailto:privacy@roomhora.com" className="text-accent hover:underline">
          privacy@roomhora.com
        </a>
        <br />
        General support:{' '}
        <a href="mailto:roomhora@gmail.com" className="text-accent hover:underline">
          roomhora@gmail.com
        </a>
      </p>
    ),
  },
];

export default function Privacy() {
  return (
    <LegalPage
      icon={Lock}
      eyebrow="Privacy"
      title="Privacy Policy"
      effectiveDate="April 22, 2026"
      summary="We collect the minimum information needed to help you find a place to live. We never sell your data. This page explains exactly what we collect, why, and the controls you have over it."
      sections={sections}
    />
  );
}
